import type {
  GlossaryDictionaryListItem,
  GlossaryDictionaryListResponse,
  GlossaryDictionaryUploadResult,
  GlossarySystemKey,
  RenameGlossaryDictionaryPayload,
  ReorderGlossaryDictionariesPayload,
  ResolveGlossaryDictionariesResponse,
  UpdateGlossaryDictionaryPayload,
} from '@ai-meeting/shared'
import { API_BASE, ApiError, apiClient, toApiError } from './api'
import { getServiceStrings } from './i18n'
import { getAccessToken } from './session'

export async function fetchGlossaryDictionaries(): Promise<GlossaryDictionaryListResponse> {
  try {
    const { data } = await apiClient.get<GlossaryDictionaryListResponse>(
      '/api/glossary/dictionaries',
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedLoadMeetings)
  }
}

export async function resolveGlossaryDictionaries(
  dictionaryIds: string[]
): Promise<ResolveGlossaryDictionariesResponse> {
  try {
    const { data } = await apiClient.post<ResolveGlossaryDictionariesResponse>(
      '/api/glossary/dictionaries/resolve',
      { dictionaryIds }
    )
    return data
  } catch (error) {
    throw toApiError(error, 'Failed to apply selected dictionaries')
  }
}

export async function updateGlossaryDictionary(
  id: string,
  payload: UpdateGlossaryDictionaryPayload,
): Promise<GlossaryDictionaryListItem> {
  try {
    const { data } = await apiClient.patch<GlossaryDictionaryListItem>(
      `/api/glossary/dictionaries/${id}`,
      payload,
    )
    return data
  } catch (error) {
    throw toApiError(error, 'Failed to update dictionary')
  }
}

export async function renameGlossaryDictionary(
  id: string,
  payload: RenameGlossaryDictionaryPayload,
): Promise<GlossaryDictionaryListItem> {
  return updateGlossaryDictionary(id, payload)
}

export async function reorderGlossaryDictionaries(
  payload: ReorderGlossaryDictionariesPayload,
): Promise<GlossaryDictionaryListItem[]> {
  try {
    const { data } = await apiClient.put<GlossaryDictionaryListItem[]>(
      '/api/glossary/dictionaries/reorder',
      payload,
    )
    return data
  } catch (error) {
    throw toApiError(error, 'Failed to reorder dictionaries')
  }
}

export async function deleteGlossaryDictionary(id: string): Promise<void> {
  try {
    await apiClient.delete(`/api/glossary/dictionaries/${id}`)
  } catch (error) {
    throw toApiError(error, 'Failed to delete dictionary')
  }
}

export async function uploadGlossaryDictionary(
  file: File,
): Promise<GlossaryDictionaryUploadResult> {
  try {
    const form = new FormData()
    form.append('file', file)
    const { data } = await apiClient.post<GlossaryDictionaryUploadResult>(
      '/api/glossary/dictionaries/upload',
      form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      },
    )
    // During a rolling deploy, the previous backend returns the same flat
    // dictionary shape without skippedRows. Normalize it for the new UI.
    return normalizeGlossaryUploadResult(data)
  } catch (error) {
    throw toApiError(error, 'Failed to upload dictionary')
  }
}

export function normalizeGlossaryUploadResult(
  data: GlossaryDictionaryListItem & {
    skippedRows?: GlossaryDictionaryUploadResult['skippedRows']
  },
): GlossaryDictionaryUploadResult {
  return {
    ...data,
    skippedRows: Array.isArray(data.skippedRows) ? data.skippedRows : [],
  }
}

async function downloadAuthorizedCsv(path: string, fallbackName: string): Promise<void> {
  try {
    const accessToken = getAccessToken()
    if (!accessToken) {
      throw new ApiError(401, getServiceStrings().authRequired)
    }

    const response = await fetch(`${API_BASE}${path}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    if (!response.ok) {
      const data = await response.json().catch(() => null)
      throw new ApiError(
        response.status,
        typeof data?.message === 'string' ? data.message : 'Failed to download file',
        {},
        typeof data?.code === 'string' ? data.code : undefined,
        data?.details,
      )
    }

    const blob = await response.blob()
    const disposition = response.headers.get('Content-Disposition') ?? ''
    const filename = parseContentDispositionFilename(disposition) || fallbackName
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
  } catch (error) {
    throw toApiError(error, 'Failed to download file')
  }
}

export function parseContentDispositionFilename(disposition: string): string | undefined {
  const extended = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(disposition)
  if (extended?.[1]) {
    try {
      return decodeURIComponent(extended[1].trim().replace(/^"|"$/g, ''))
    } catch {
      // Fall through to the ASCII filename when the extended value is malformed.
    }
  }
  return /filename\s*=\s*"([^"]+)"/i.exec(disposition)?.[1]
}

export async function downloadGlossaryDictionary(
  id: string,
  name: string,
): Promise<void> {
  await downloadAuthorizedCsv(
    `/api/glossary/dictionaries/${id}/download`,
    `${name}.csv`,
  )
}

export async function downloadSystemGlossaryDictionary(
  systemKey: GlossarySystemKey,
  name: string,
): Promise<void> {
  await downloadAuthorizedCsv(`/api/glossary/system/${systemKey}/download`, `${name}.csv`)
}

export async function downloadGlossaryTemplate(): Promise<void> {
  await downloadAuthorizedCsv('/api/glossary/template', 'glossary-template.csv')
}
